import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from '#client/components/features/LabelPrinting/utils/styleSearch';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Debounced master-product suggestions.
 *
 * React Query keys the cache on the debounced term, so a superseded request can never
 * overwrite a newer one — the newer key simply wins.
 */
export function useStyleSuggest({ term, searchType }) {
    const [debouncedTerm, setDebouncedTerm] = useState('');

    useEffect(() => {
        const trimmed = String(term ?? '').trim();
        const timer = setTimeout(() => setDebouncedTerm(trimmed), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [term]);

    const enabled = debouncedTerm.length >= MIN_SEARCH_LENGTH;

    const query = useQuery({
        queryKey: ['style-suggest', searchType, debouncedTerm],
        enabled,
        queryFn: async () => {
            const response = await axios.get(`${API_URL}/api/app/products/style-suggest?term=${encodeURIComponent(debouncedTerm)}&searchType=${searchType}`, { withCredentials: true });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to search styles');
            return response.data.data;
        },
    });

    return {
        debouncedTerm,
        enabled,
        isLoading: query.isFetching,
        matches: query.data?.matches ?? [],
        hasMore: Boolean(query.data?.hasMore),
        error: query.error,
    };
}
